import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, PhoneCall } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useBranding } from '../lib/branding';
import { Button, Card, Field, Input, Select, Spinner, Textarea } from '../components/ui';

/**
 * Reporte de un efecto no deseado.
 *
 * Es la pantalla más delicada de la app. Dos decisiones de diseño que no son
 * cosméticas:
 *
 *  · El teléfono de urgencias va arriba de todo. Alguien que está mal no debería
 *    tener que completar un formulario para encontrar a quién llamar.
 *  · El texto deja claro que esto no reemplaza una consulta. Un paciente que
 *    reporta acá y se queda esperando respuesta es exactamente lo que hay que
 *    evitar.
 */
export function AdverseEventPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const branding = useBranding();

  const [description, setDescription] = useState('');
  const [drugName, setDrugName] = useState('');
  const [onsetDate, setOnsetDate] = useState('');
  const [severity, setSeverity] = useState('MEDIUM');
  const [hospitalized, setHospitalized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const report = useMutation({
    mutationFn: () =>
      api.post<{ id: string; message: string }>('/me/adverse-events', {
        description: description.trim(),
        drugName: drugName.trim() || undefined,
        onsetDate: onsetDate || undefined,
        severity,
        requiredHospitalization: hospitalized,
      }),
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : 'No pudimos enviar tu reporte');
    },
  });

  if (report.isSuccess) {
    return (
      <div className="max-w-lg lg:max-w-3xl mx-auto px-5 py-16 text-center">
        <div className="size-16 rounded-full bg-success-soft text-success grid place-items-center mx-auto">
          <CheckCircle2 className="size-8" />
        </div>
        <h1 className="text-2xl font-semibold mt-5 tracking-tight">Reporte enviado</h1>
        <p className="text-fg-muted mt-3 leading-relaxed">{report.data.message}</p>
        <Button className="mt-8" fullWidth onClick={() => navigate(`/${slug}/app`)}>
          Volver al inicio
        </Button>
      </div>
    );
  }

  if (report.isPending) return <Spinner label="Enviando tu reporte…" />;

  return (
    <div className="max-w-lg lg:max-w-3xl mx-auto">
      <header className="bg-surface-raised border-b border-border px-5 pt-4 pb-4 safe-top">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg mb-3"
        >
          <ArrowLeft className="size-4" />
          Volver
        </button>
        <h1 className="text-xl font-semibold tracking-tight">Reportar un efecto</h1>
        <p className="text-sm text-fg-muted mt-1 leading-relaxed">
          Contanos qué te pasó. Tu equipo lo revisa y lo deriva al área que corresponde.
        </p>
      </header>

      <div className="px-5 py-5 space-y-5">
        <Card className="p-4 bg-danger-soft border-danger">
          <p className="font-semibold text-danger">Si te sentís mal ahora, no uses este formulario</p>
          <p className="text-sm mt-1.5 leading-relaxed">
            Ante fiebre, falta de aire, dolor intenso o cualquier síntoma que te preocupe, comunicate
            directamente con tu equipo o acercate a una guardia.
          </p>
          {branding.branding?.supportPhone && (
            <a href={`tel:${branding.branding.supportPhone}`} className="block mt-3">
              <Button variant="danger" fullWidth>
                <PhoneCall className="size-4" />
                Llamar al {branding.branding.supportPhone}
              </Button>
            </a>
          )}
        </Card>

        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            report.mutate();
          }}
        >
          <Field
            label="¿Qué te pasó?"
            required
            hint="Contá con tus palabras qué sentiste, desde cuándo y qué tan fuerte fue."
            htmlFor="description"
          >
            <Textarea
              id="description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={6}
              required
              minLength={10}
              placeholder="Por ejemplo: me apareció un sarpullido en los brazos hace tres días, pica bastante de noche…"
            />
          </Field>

          <Field label="¿Con qué medicación lo relacionás?" htmlFor="drug">
            <Input
              id="drug"
              value={drugName}
              onChange={(event) => setDrugName(event.target.value)}
              placeholder="Si no estás seguro, dejalo vacío"
            />
          </Field>

          <Field label="¿Cuándo empezó?" htmlFor="onset">
            <Input
              id="onset"
              type="date"
              max={new Date().toISOString().slice(0, 10)}
              value={onsetDate}
              onChange={(event) => setOnsetDate(event.target.value)}
            />
          </Field>

          <Field label="¿Qué tan intenso fue?" htmlFor="severity" required>
            <Select
              id="severity"
              value={severity}
              onChange={(event) => setSeverity(event.target.value)}
            >
              <option value="LOW">Leve — molesto, pero pude seguir con mi día</option>
              <option value="MEDIUM">Moderado — me limitó algunas actividades</option>
              <option value="HIGH">Intenso — no pude hacer mi rutina</option>
              <option value="CRITICAL">Muy grave — necesité atención urgente</option>
            </Select>
          </Field>

          <label className="flex items-start gap-3 p-4 rounded-md border border-border bg-surface-raised cursor-pointer">
            <input
              type="checkbox"
              checked={hospitalized}
              onChange={(event) => setHospitalized(event.target.checked)}
              className="mt-1 size-5 accent-[var(--tr-primary)]"
            />
            <span className="text-[15px] leading-snug">
              Tuve que internarme o ir a una guardia por esto
            </span>
          </label>

          {error && (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          )}

          <Button type="submit" size="lg" fullWidth disabled={description.trim().length < 10}>
            Enviar el reporte
          </Button>

          <p className="text-xs text-fg-subtle text-center leading-relaxed">
            Este reporte se deriva al área de farmacovigilancia de la institución, como exige la
            normativa sanitaria. No reemplaza la consulta con tu médico.
          </p>
        </form>
      </div>
    </div>
  );
}
